import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import { NATIONALITY_CODE_SET } from '@/constants/checkout/nationalities';
import { COUNTRY_CODE_SET } from '@/constants/checkout/countries';
import { parseGermanDateLocal } from '@/utils/formatting/dateParser';
import { parseLenientGermanDate } from '@/utils/formatting/dateInput';

/**
 * Validation constants for checkout forms
 */

// Age limits for validation
export const MAX_AGE = 110;
export const ADULT_MIN_AGE = 18;
export const CHILD_MAX_AGE = 17;

/**
 * Calculate age from birth date
 * @param birthDate - Date of birth
 * @returns Age in years
 */
export const calculateAge = (birthDate: Date): number => {
  return calculateAgeAtDate(birthDate, new Date());
};

/**
 * Calculate age at a specific reference date.
 *
 * Used by the Adults/Children/Infants checkout validation, which must evaluate a
 * traveller's age **at the travel (check-in) date** — not today — because the hotel's
 * age band is checked against the check-in date (a child who is 12 at booking but 13
 * at check-in is rejected as a child). `calculateAge` is `calculateAgeAtDate(_, today)`.
 *
 * @param birthDate - Date of birth
 * @param referenceDate - Date at which to evaluate the age
 * @returns Age in years at the reference date
 */
export const calculateAgeAtDate = (birthDate: Date, referenceDate: Date): number => {
  let age = referenceDate.getFullYear() - birthDate.getFullYear();
  const monthDiff = referenceDate.getMonth() - birthDate.getMonth();

  // Adjust if birthday hasn't occurred by the reference date yet
  if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
};

/**
 * Phone number validator
 * Requires an international number (`+49 160 1234567`) that is valid for its country.
 */
export const phoneValidator = z
  .string()
  .min(1, 'ibe.validation.phone.required')
  .refine((value) => isValidPhoneNumber(value), 'ibe.validation.phone.invalid-format');

/**
 * Birth date validator
 * Format: DD.MM.YYYY
 * Validates format, date validity, and age range
 */
export const birthDateValidator = z
  .string()
  .min(1, 'ibe.validation.birthDate.required')
  .refine((date) => {
    const regex = /^\d{2}\.\d{2}\.\d{4}$/;
    return regex.test(date);
  }, 'ibe.validation.birthDate.invalid-format')
  .refine((date) => parseLenientGermanDate(date) !== null, 'ibe.validation.birthDate.invalid')
  .refine((date) => {
    const age = calculateAge(parseGermanDateLocal(date));
    return age >= 0 && age <= MAX_AGE;
  }, 'ibe.validation.birthDate.age-range');

/**
 * Country validator — required lowercase ISO 3166-1 alpha-2 code.
 * The full country set lives in constants/checkout/countries.ts (shared with the
 * nationality list). Sent to DaVinci as Client/Participant Address.Country.
 */
export const countryValidator = z
  .string({ required_error: 'ibe.validation.country.required' })
  .min(1, 'ibe.validation.country.required')
  .refine((code) => COUNTRY_CODE_SET.has(code), 'ibe.validation.country.required');
export type Country = z.infer<typeof countryValidator>;

/**
 * Salutation enum - form of address
 */
export const SalutationEnum = z.enum(['mr', 'mrs', 'diverse'], {
  errorMap: () => ({ message: 'ibe.validation.salutation.required' }),
});
export type Salutation = z.infer<typeof SalutationEnum>;

/**
 * Gender enum - biological sex sent to DaVinci as the numeric Gender field.
 * Used for children (adults derive Gender from their salutation instead).
 * Mapped to DaVinci Gender in mapGenderToDaVinci(): male/diverse → 0, female → 1.
 */
export const GenderEnum = z.enum(['male', 'female', 'diverse'], {
  errorMap: () => ({ message: 'ibe.validation.gender.required' }),
});
export type Gender = z.infer<typeof GenderEnum>;

/**
 * Nationality validator — required ISO 3166-1 alpha-2 code (uppercase).
 * Sent to DaVinci as Participant.Passport.NationalityCode; the matching German
 * country name is sent as Participant.Passport.Nationality (see nationalities.ts).
 */
export const nationalityValidator = z
  .string({ required_error: 'ibe.validation.nationality.required' })
  .min(1, 'ibe.validation.nationality.required')
  .refine((code) => NATIONALITY_CODE_SET.has(code), 'ibe.validation.nationality.invalid');
